/**
 * cockpit.png is 1920×1200 (AR 1.6). The cockpit container is 16∶11 (AR ≈ 1.45).
 * With background-size: cover + bottom center the image is height-fitted and
 * side-cropped — horizontal positions must be converted from image-space to
 * container-space.
 */
const IMAGE_AR = 1920 / 1200;
const CONTAINER_AR = 16 / 11;
const CROP_X =
  IMAGE_AR > CONTAINER_AR ? ((IMAGE_AR / CONTAINER_AR - 1) / (IMAGE_AR / CONTAINER_AR)) * 50 : 0;

/** Map an x% coordinate on cockpit.png to the displayed container. */
export function cockpitX(imgX: number): number {
  const visible = 100 - 2 * CROP_X;
  return ((imgX - CROP_X) / visible) * 100;
}

export interface CockpitLightDef {
  id: string;
  left: number;
  top: number;
  width: string;
  height: string;
  color: "blue" | "pink";
  shape: "dot" | "pill" | "square";
  animation: "blink" | "pulse" | "flicker";
  duration: number;
  delay: number;
}

export interface CockpitRadarDef {
  id: string;
  left: number;
  top: number;
  width: string;
  height: string;
  shape: "rect" | "circle";
  duration: number;
  delay: number;
}

function light(
  id: string,
  imgX: number,
  imgY: number,
  width: string,
  height: string,
  color: "blue" | "pink",
  shape: "dot" | "pill" | "square",
  animation: "blink" | "pulse" | "flicker",
  duration: number,
  delay: number
): CockpitLightDef {
  return {
    id,
    left: cockpitX(imgX),
    top: imgY,
    width,
    height,
    color,
    shape,
    animation,
    duration,
    delay,
  };
}

function radar(
  id: string,
  imgX: number,
  imgY: number,
  imgW: number,
  imgH: number,
  shape: "rect" | "circle",
  duration: number,
  delay: number
): CockpitRadarDef {
  const visible = 100 - 2 * CROP_X;
  return {
    id,
    left: cockpitX(imgX),
    top: imgY,
    width: `${(imgW / visible) * 100}%`,
    height: `${imgH}%`,
    shape,
    duration,
    delay,
  };
}

/** Percentage positions in cockpit.png image-space, converted for display. */
export const COCKPIT_LIGHTS: Record<string, CockpitLightDef[]> = {
  ruimtereis: [
    /* overhead strip */
    light("top-pink-l", 44.3, 8.0, "0.5rem", "0.28rem", "pink", "pill", "blink", 1.4, 0.2),
    light("top-blue-1", 47.9, 8.0, "0.38rem", "0.38rem", "blue", "dot", "pulse", 2.1, 0),
    light("top-blue-2", 50.0, 8.0, "0.38rem", "0.38rem", "blue", "dot", "blink", 1.8, 0.6),
    light("top-blue-3", 52.1, 8.0, "0.38rem", "0.38rem", "blue", "dot", "pulse", 2.4, 1.1),
    light("top-pink-r", 55.7, 8.0, "0.5rem", "0.28rem", "pink", "pill", "flicker", 2.8, 0.4),

    /* side pillars */
    light("pillar-l-blue", 4.1, 25.8, "0.4rem", "0.4rem", "blue", "dot", "pulse", 2, 0.3),
    light("pillar-l-pink", 4.1, 31.3, "0.48rem", "0.26rem", "pink", "pill", "blink", 1.6, 0.8),
    light("pillar-r-blue", 95.9, 25.8, "0.4rem", "0.4rem", "blue", "dot", "blink", 1.9, 0.5),
    light("pillar-r-pink", 95.9, 31.3, "0.48rem", "0.26rem", "pink", "pill", "pulse", 2.2, 1.2),

    /* left dashboard */
    light("dash-l-pink-1", 11.5, 69.2, "0.48rem", "0.48rem", "pink", "dot", "blink", 1.5, 0.1),
    light("dash-l-blue-1", 15.6, 69.2, "0.42rem", "0.42rem", "blue", "dot", "pulse", 2.3, 0.7),
    light("dash-l-pink-2", 11.5, 74.2, "0.48rem", "0.48rem", "pink", "dot", "flicker", 3.2, 0.3),
    light("dash-l-blue-2", 15.6, 74.2, "0.42rem", "0.42rem", "blue", "dot", "blink", 1.7, 1),
    light("dash-l-main", 13.5, 83.3, "0.65rem", "0.65rem", "pink", "dot", "pulse", 2.6, 0),

    /* center console — flanking buttons */
    light("dash-c-l-1", 34.1, 60.5, "0.36rem", "0.36rem", "blue", "square", "blink", 1.3, 0.2),
    light("dash-c-l-2", 34.1, 65.8, "0.36rem", "0.36rem", "pink", "square", "pulse", 2, 0.9),
    light("dash-c-l-3", 34.1, 71.1, "0.36rem", "0.36rem", "blue", "square", "flicker", 2.5, 0.4),
    light("dash-c-l-4", 34.1, 76.4, "0.36rem", "0.36rem", "pink", "square", "blink", 1.6, 1.3),
    light("dash-c-r-1", 65.9, 60.5, "0.36rem", "0.36rem", "pink", "square", "pulse", 2.1, 0.6),
    light("dash-c-r-2", 65.9, 65.8, "0.36rem", "0.36rem", "blue", "square", "blink", 1.4, 0),
    light("dash-c-r-3", 65.9, 71.1, "0.36rem", "0.36rem", "pink", "square", "blink", 1.8, 0.8),
    light("dash-c-r-4", 65.9, 76.4, "0.36rem", "0.36rem", "blue", "square", "flicker", 3, 1.1),

    /* center bottom row */
    light("dash-c-b-1", 42.2, 83.5, "0.32rem", "0.32rem", "blue", "square", "blink", 1.2, 0),
    light("dash-c-b-2", 45.3, 83.5, "0.32rem", "0.32rem", "blue", "square", "pulse", 2.4, 0.5),
    light("dash-c-b-3", 48.5, 83.5, "0.32rem", "0.32rem", "blue", "square", "blink", 1.5, 1),
    light("dash-c-b-4", 51.6, 83.5, "0.32rem", "0.32rem", "blue", "square", "flicker", 2.7, 0.3),
    light("dash-c-b-5", 54.7, 83.5, "0.32rem", "0.32rem", "blue", "square", "blink", 1.7, 0.7),
    light("dash-c-b-6", 57.8, 83.5, "0.32rem", "0.32rem", "blue", "square", "pulse", 2, 1.2),

    /* right dashboard */
    light("dash-r-pink-1", 79.7, 65.4, "0.46rem", "0.46rem", "pink", "dot", "blink", 1.6, 0.2),
    light("dash-r-blue-1", 83.9, 65.4, "0.42rem", "0.42rem", "blue", "dot", "pulse", 2.2, 0.9),
    light("dash-r-pink-2", 88.5, 71.7, "0.46rem", "0.46rem", "pink", "dot", "flicker", 2.9, 0.5),
    light("dash-r-blue-2", 92.4, 71.7, "0.42rem", "0.42rem", "blue", "dot", "blink", 1.4, 1.1),
    light("dash-r-grid-1", 89.8, 81.3, "0.3rem", "0.3rem", "pink", "square", "blink", 1.3, 0),
    light("dash-r-grid-2", 93.0, 81.3, "0.3rem", "0.3rem", "blue", "square", "pulse", 2.5, 0.6),
    light("dash-r-grid-3", 89.8, 85.4, "0.3rem", "0.3rem", "blue", "square", "blink", 1.8, 1.3),
    light("dash-r-grid-4", 93.0, 85.4, "0.3rem", "0.3rem", "pink", "square", "flicker", 3.1, 0.4),
  ],
};

export const COCKPIT_RADARS: Record<string, CockpitRadarDef[]> = {
  ruimtereis: [
    /* main rectangular radar screen */
    radar("radar-center", 50, 70.3, 26.2, 17.5, "rect", 4, 0),
    /* circular radar on right panel */
    radar("radar-right", 82.6, 72.5, 7.6, 7.6, "circle", 3.2, 1.4),
  ],
};
